/**
 * @module common/components/pinch_zoom_pan
 */
import React, { Component, PropTypes } from 'react';
import throttle from 'lodash/throttle';
import Observable from 'rx-lite';

export const between = (min, max, val) => (
  Math.min(max, Math.max(min, val))
);

/**
 * Function called to recalculate new image position when it's zoomed.
 */
export const repositionImage = (x, zoom, imageSize, elemWidth, displacementX) => {
  if (zoom === 1 || imageSize.width * zoom < elemWidth) {
    return {
      x: 0,
    };
  }

  const posX = x || 0;
  const maxX = -((imageSize.width * zoom) - elemWidth) / (2 * zoom);
  const minX = -maxX;
  const xDisplacement = displacementX || posX - (posX * zoom);
  const translateX = posX ? posX + xDisplacement : xDisplacement;

  return {
    x: between(maxX, minX, translateX),
  };
};

function isTouch() {
  return (('ontouchstart' in window) ||
    (navigator.MaxTouchPoints > 0) ||
    (navigator.msMaxTouchPoints > 0));
}

function eventPreventDefault(event, zoom) {
  if (zoom !== 1 || isTouch()) {
    event.preventDefault();
  }
}

function hasTwoTouchPoints(event) {
  if (isTouch()) {
    return event.touches && event.touches.length === 2;
  }

  return event.altKey;
}

function normalizeTouch(e) {
  const p = isTouch() ? e.touches[0] : e;
  return {
    x: p.clientX,
    y: p.clientY
  };
}

/**
 * Inner component that handles image zooming.
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
export default class ReactPinchZoomPan extends Component {
  constructor(props) {
    super(props);
    this.state = {
      zoom: null,
      size: {},
    };
  }

  componentDidMount() {
    this.handlePinch();
    this.resize();
    window.addEventListener('resize', throttle(() => this.resize(), 500));
  }

  componentWillUnmount() {
    if (this.pinchSubscription) {
      this.pinchSubscription.dispose();
    }
    window.removeEventListener('resize', this.resize);
  }

  resize() {
    if (this.refs.root) {
      const domNode = this.refs.root;
      this.setState({
        size: {
          width: domNode.offsetWidth,
          height: domNode.offsetHeight,
        }
      });
    }
  }

  handlePinch() {
    const { maxScale, onPositionChange } = this.props;
    const domNode = this.refs.root;
    const touchStart = Rx.Observable.fromEvent(domNode, (isTouch()) ? 'touchstart' : 'mousedown');
    const touchMove = Rx.Observable.fromEvent(window, (isTouch()) ? 'touchmove' : 'mousemove');
    const touchEnd = Rx.Observable.fromEvent(window, (isTouch()) ? 'touchend' : 'mouseup');

    function translatePos(point, size) {
      return {
        x: (point.x - (size.width / 2)),
        y: (point.y - (size.height / 2))
      }
    }

    const pinch = touchStart
    .tap(e => {
      eventPreventDefault(e, this.state.zoom || this.props.zoom);
    }).flatMap((md) => {
      const startPoint = normalizeTouch(md);
      const { size } = this.state;

      return touchMove
      .map((mm) => {
        const { x, y } = this.props.imagePositions;
        const { imageSize } = this.props;
        const movePoint = normalizeTouch(mm);
        const maxZoom = maxScale * 0.25;
        const minZoom = 0.25;
        let retObj = null;
        let { zoom } = this.state;

        if (!zoom) {
          zoom = this.props.zoom;
        }

        if (hasTwoTouchPoints(mm)) {
          if (isTouch()) {
            let scaleFactor = mm.scale ? mm.scale : (movePoint.x < (size.width / 2)) ? zoom + ((translatePos(startPoint, size).x - translatePos(movePoint, size).x) / size.width) : zoom + ((translatePos(movePoint, size).x - translatePos(startPoint, size).x) / size.width);
            scaleFactor = between(minZoom, maxZoom, scaleFactor);

            retObj = {
              zoom: scaleFactor,
              x: (scaleFactor < 1.01) ? 0 : x,
              y: (scaleFactor < 1.01) ? 0 : y,
            };
          }
        } else {
          if (!isTouch() && (imageSize.width * zoom > size.width)) {
            const displacementX = movePoint.x - startPoint.x;
            if (zoom !== 1) {
              retObj = { ...repositionImage(x, zoom, imageSize, size.width, displacementX) };
            }
          } else if (isTouch()) {
            const sizeCoefficient = size.width / imageSize.width;
            const scaleFactorX = ((imageSize.width * sizeCoefficient * zoom) - size.width) / (zoom * 2);
            const scaleFactorY = ((imageSize.height * sizeCoefficient * zoom) - size.height) / (zoom * 2);

            return {
              x: between(-(scaleFactorX), scaleFactorX, movePoint.x - startPoint.x),
              y: between(-(scaleFactorY), 0, movePoint.y - startPoint.y),
            };
          }
        }

        return retObj;
      })
      .takeUntil(touchEnd);
    });

    this.pinchSubscription = pinch.subscribe((newObject) => {
      global.requestAnimationFrame(() => {
        if (!newObject) {
          return;
        }

        if (newObject.zoom) {
          this.setState({
            ...this.state,
            zoom: newObject.zoom,
          });
        }
        onPositionChange(newObject);
      });
    });
  }

  render() {
    const { size } = this.state;
    let { zoom } = this.state;
    const { imagePositions, imageSize } = this.props;
    const x = imagePositions.x;
    const y = imagePositions.y;

    if (!zoom) {
      zoom = this.props.zoom;
    }

    return (
      <div ref="root" className="row expanded revision-image">
        {this.props.render({
          x: x.toFixed(2),
          y: y.toFixed(2),
          zoom: zoom.toFixed(2),
          size: { ...size },
          imageSize,
        })}
      </div>
    );
  }
}

/**
 * PropTypes.
 *
 * @property {object} imageSize - object with width and height of the displayed image
 * @property {object} imagePositions - current position of panned image
 * @property {number} maxScale - float number defining the max zoom limit. We assume the min zoom value is
 *                                1 / half of zooming levels, so in case of 8 zooming levels it's 0.25.
 * @property {number} zoom - current image zoom
 * @property {func} onPositionChange - function called when image is panned
 * @property {func} render - render function for this element provided by parent component
 */
ReactPinchZoomPan.propTypes = {
  imageSize: PropTypes.object,
  imagePositions: PropTypes.object,
  maxScale: PropTypes.number,
  zoom: PropTypes.number,
  onPositionChange: PropTypes.func,
  render: PropTypes.func,
};



// WEBPACK FOOTER //
// ./common/components/pinch_zoom_pan.js